import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Badge,
  OverlayTrigger,
  Tooltip,
  Popover,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import { gql, useQuery, useMutation } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import AddUserWizard from "../../components/Wizards/AddUserWizard";
import "../../starek.css";
import ConfirmModal from "../../components/common/ConfirmModal";

const GET_USERS = gql`
  query Users {
    users {
      id
      email
      name
      username
      is_active
      description
      home_timezone
      current_timezone
      show_time_in_home_timezone
      identities {
        provider
      }
      memberships {
        plant {
          id
          name
        }
        role {
          id
          name
        }
      }
    }
  }
`;

const DELETE_ONE_USER = gql`
  mutation deleteOneUser($id: Int!) {
    deleteOneUser(where: { id: $id }) {
      id
    }
  }
`;

const DELETE_MANY_USER = gql`
  mutation deleteManyUser($ids: [Int!]!) {
    deleteManyUser(where: { id: { in: $ids } }) {
      count
    }
  }
`;

const formatIdentityProviders = (row) =>
  (row?.identities || []).map((i) => i?.provider).filter(Boolean);

const formatMemberships = (row) =>
  (row?.memberships || [])
    .map((m) => {
      const plant = m?.plant?.name || "-";
      const role = m?.role?.name || "-";
      return `${plant}: ${role}`;
    })
    .filter(Boolean);

const isOnlyLocalIdentity = (row) => {
  const providers = formatIdentityProviders(row);
  return (
    providers.length > 0 &&
    providers.every((p) => p === "local-identity-provider")
  );
};

const Users = () => {
  const starekContext = useContext(StarekContext);

  const { data, loading, error, refetch } = useQuery(GET_USERS, {
    fetchPolicy: "network-only",
  });

  const [items, setItems] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOneUser] = useMutation(DELETE_ONE_USER);
  const [deleteManyUser] = useMutation(DELETE_MANY_USER);

  useEffect(() => {
    if (error) starekContext.showError(error);
  }, [error]);

  useEffect(() => {
    if (!loading && data?.users) {
      setItems(data.users);
    }
  }, [data, loading]);

  const customStyles = {
    headCells: { style: { fontWeight: "bold" } },
    rows: { style: { minHeight: "60px" } },
    cells: {
      style: {
        paddingTop: "12px",
        paddingBottom: "12px",
        whiteSpace: "normal",
        lineHeight: "1.2rem",
      },
    },
  };

  const filteredItems = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((u) => {
      const membershipsText = formatMemberships(u).join(" ").toLowerCase();
      const plantNames = (u?.memberships || [])
        .map((m) => m?.plant?.name || "")
        .join(" ")
        .toLowerCase();
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        membershipsText.includes(q) ||
        plantNames.includes(q) ||
        (u.description || "").toLowerCase().includes(q)
      );
    });
  }, [items, filterText]);

  const isSingle = selectedRows.length === 1;
  const hasSelection = selectedRows.length > 0;
  const localOnlySelection =
    hasSelection && selectedRows.every(isOnlyLocalIdentity);
  const canDelete = hasSelection && localOnlySelection;
  const anyNonLocalSelected =
    hasSelection && selectedRows.some((r) => !isOnlyLocalIdentity(r));
  const showDeleteTooltip = anyNonLocalSelected;
  const onClickEdit = () => {
    if (!isSingle) return;
    setEditingUser(selectedRows[0]);
    setShowWizard(true);
  };

  const onClickAdd = () => {
    setEditingUser(null);
    setShowWizard(true);
  };

  const handleDelete = async () => {
    const ids = selectedRows.map((u) => Number(u.id));
    if (ids.length === 1) {
      await deleteOneUser({ variables: { id: ids[0] } });
    } else {
      await deleteManyUser({ variables: { ids } });
    }
    await refetch();
    setConfirmOpen(false);
    setToggleCleared((p) => !p);
    setSelectedRows([]);
    try {
      starekContext?.showSuccess?.("User(s) deleted");
    } catch {}
  };

  const NameCell = ({ row }) => (
    <span role="button" className="text-primary">
      {row.name || "-"}
    </span>
  );

  const MembershipsCell = ({ row }) => {
    const memberships = formatMemberships(row);
    const firstTwo = memberships.slice(0, 2);
    const extra = memberships.length - firstTwo.length;

    const pop = (
      <Popover id={`membership-pop-${row.id}`}>
        <Popover.Body className="p-2">
          {memberships.length === 0 ? (
            <div className="text-muted">No memberships</div>
          ) : (
            memberships.map((m, i) => (
              <div key={i} className="py-1">
                {m}
              </div>
            ))
          )}
        </Popover.Body>
      </Popover>
    );

    return (
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
        {firstTwo.map((m) => (
          <Badge key={m} bg="light" text="dark" className="border">
            {m}
          </Badge>
        ))}
        {extra > 0 && (
          <OverlayTrigger
            trigger="click"
            placement="right"
            overlay={pop}
            rootClose
          >
            <Badge bg="light" text="dark" className="border" role="button">
              +{extra} more
            </Badge>
          </OverlayTrigger>
        )}
      </div>
    );
  };

  const IdentityProvidersCell = ({ row }) => {
    const providers = formatIdentityProviders(row);
    return (
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
        {providers.map((p) => (
          <Badge key={p} bg="light" text="dark" className="border">
            {p}
          </Badge>
        ))}
      </div>
    );
  };

  const columns = [
    {
      name: "Name",
      selector: (r) => r.name || "-",
      sortable: true,
      cell: (row) => <NameCell row={row} />,
      grow: 1.1,
    },
    {
      name: "Email",
      selector: (r) => r.email || "-",
      sortable: true,
      grow: 1.2,
    },
    {
      name: "Memberships",
      selector: (r) => formatMemberships(r).join(", "),
      sortable: false,
      cell: (row) => <MembershipsCell row={row} />,
      grow: 1.6,
    },
    {
      name: "Identity Provider",
      selector: (r) => formatIdentityProviders(r).join(", "),
      sortable: false,
      cell: (row) => <IdentityProvidersCell row={row} />,
      grow: 1.2,
    },
    {
      name: "Description",
      selector: (r) => r.description || "-",
      sortable: false,
      cell: (row) => (
        <div className="text-truncate" style={{ maxWidth: 420 }}>
          {row.description || "-"}
        </div>
      ),
      grow: 2,
    },
  ];

  if (loading) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-xs d-flex justify-content-center">
          <i className="fa fa-user fa-10x text-success"></i>
        </div>
        <Spinner />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div
          className="d-flex align-items-center flex-grow-1"
          style={{ gap: 12 }}
        >
          <input
            type="text"
            className="border rounded p-2"
            style={{ width: "40%" }}
            placeholder="Search  —  Name, email, membership, etc..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <div className="d-flex align-items-center">
          <Button
            className="me-2"
            variant={isSingle ? "outline-primary" : "secondary"}
            disabled={!isSingle}
            onClick={onClickEdit}
          >
            Edit
          </Button>

          {showDeleteTooltip ? (
            <OverlayTrigger
              placement="bottom"
              trigger={["hover", "focus"]}
              overlay={
                <Tooltip id="delete-tooltip">
                  Please select local users only
                </Tooltip>
              }
            >
              <span className="d-inline-block me-2" tabIndex="0">
                <Button variant="danger" disabled>
                  Delete
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <span className="d-inline-block me-2" tabIndex="0">
              <Button
                variant="danger"
                disabled={!canDelete}
                onClick={() => setConfirmOpen(true)}
              >
                Delete
              </Button>
            </span>
          )}

          <Button variant="primary" onClick={onClickAdd}>
            Add User
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredItems}
        selectableRows
        onSelectedRowsChange={({ selectedRows }) =>
          setSelectedRows(selectedRows)
        }
        striped
        clearSelectedRows={toggleCleared}
        highlightOnHover
        pointerOnHover
        responsive
        pagination
        dense
        selectableRowsHighlight
        selectableRowsNoSelectAll={false}
        customStyles={customStyles}
      />

      <AddUserWizard
        show={showWizard}
        mode={editingUser ? "edit" : "add"}
        initialValues={editingUser || {}}
        onClose={() => {
          setShowWizard(false);
          setEditingUser(null);
          setToggleCleared((p) => !p);
        }}
        onFinish={async () => {
          setShowWizard(false);
          setEditingUser(null);
          await refetch();
          setToggleCleared((p) => !p);
        }}
      />

      <ConfirmModal
        open={confirmOpen}
        title={
          selectedRows.length === 1
            ? "Delete user?"
            : `Delete ${selectedRows.length} users?`
        }
        message={
          selectedRows.length === 1
            ? `You're about to permanently delete "${
                selectedRows[0]?.name || selectedRows[0]?.email
              }".`
            : "You're about to permanently delete the selected users."
        }
        confirmText="Delete"
        cancelText="Cancel"
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export { Users };
